/* eslint-disable max-len */
/* eslint-disable import/prefer-default-export */
import { parse as qsParse } from 'query-string';
import { getCookie, setCookie } from '@aarhus-university/au-lib-react-components/src/lib/helpers';

const roles = {
  impersonator: 'IR-BachelorImpersonator',
  applicant: 'IR-BachelorAnsoeger',
};

export const cookies = {
  activation: 'bachelor-application-activated',
  initialLang: 'bachelor-application-initial-lang',
  userType: 'bachelor-application-user-type',
  mitstudie: 'mitstudie-login',
  quota2: 'bachelor-application-quota2',
};

export const isActivated = (): boolean => {
  const qs = qsParse(window.location.search);
  const activationCookie = getCookie(cookies.activation);
  const mitstudieLogin = getCookie(cookies.mitstudie);
  const { activated } = qs;
  return activated === 'true' || activationCookie === 'true' || mitstudieLogin === 'true';
};

export const setActivatedCookie = (): void => {
  setCookie(cookies.activation, true, null, 365, `;path=/;domain=${window.location.hostname}`);
};

export const setInitialLanguageCookie = (lang: string | null): void => {
  setCookie(cookies.initialLang, lang, null, 365, `;path=/;domain=${window.location.hostname}`);
};

export const killInitialLanguageCookie = (): void => {
  setCookie(cookies.initialLang, null, null, -1, `;path=/;domain=${window.location.hostname}`);
};

export const getInitialLangCookie = (): string | null => getCookie(cookies.initialLang) as string;

export const hasAnswered = (applications: IApplication[] | null): boolean => {
  if (!applications) {
    return false;
  }
  const offered = applications.filter((a) => a.response.offered);
  if (offered.length > 1 && offered.filter((a) => a.response.offerAccepted).length > 1) {
    return false;
  }
  const accepted = offered.filter((a) => a.response.offerAccepted).length > 0;
  const rejected = offered.filter((a) => a.response.offerAccepted === false).length > 0;
  return accepted || rejected;
};

export const setActiveItemNav = (always = false): void => {
  const nav = document.querySelector('header nav');
  if (nav) {
    if (always || nav.querySelector('.nav__item--active')) {
      nav.classList.add('nav--has-active-item');
    }
  }
};

const getNumberWithOrdinal = (n: number, lang: string): string => {
  if (lang === 'da') {
    return `${n}.`;
  }
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const PRIORITY_MAX = 20;
export const getPriority = (priority: number, lang: string): string => getNumberWithOrdinal(priority - (priority > PRIORITY_MAX ? PRIORITY_MAX : 0), lang);

export const isAllowed = (groups:AU.IAdGroup[]): boolean => groups.map((group) => group.name).includes(roles.applicant);

export const getDetailsCount = (application: IApplication): number => {
  let count = 0;
  if (application.response.quota1RecalculatedQuotient) {
    count += 1;
  }
  if (application.response.quota2Quotient && application.request.quota2) {
    count += 1;
  }
  if (application.request.delayed) {
    count += 1;
  }
  if (application.request.standby) {
    count += 1;
  }
  return count;
};

export const parseQuota2Calculation = (quota2Calculation: string): parseQuota2CalculationResult => {
  const subjects: {
    name: string;
    grades: number[];
  }[] = [];
  const regex = /([A-ZÆØÅ]+(?:\s[A-Z])?)\s*\(\s*([^)]+)\s*\)/g;
  const str = quota2Calculation;
  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(str)) !== null) {
    const subject = match[1];
    const grades = match[2].replace('(', '').replace(')', '').split('+');
    subjects.push({
      name: subject,
      grades: grades.map((m: string) => parseInt(m, 10)),
    });
  }
  return {
    maxGrades: Math.max(...subjects.map((s) => s.grades.length)),
    subjects,
  };
};

export const getNotificationType = (type: number): string => {
  let typeStr = 'default';
  if (type === 0) {
    typeStr = 'warning';
  }
  if (type === 1) {
    typeStr = 'attention';
  }
  if (type === 2) {
    typeStr = 'confirm';
  }
  return typeStr;
};

export const getCalloutClass = (type: number): string => {
  let className = '';
  if (type === 0) {
    className = ' header-callout--warning';
  }
  if (type === 1) {
    className = ' header-callout--attention';
  }
  if (type === 2) {
    className = ' header-callout--confirm';
  }
  return className;
};

export const showQuota2Info = (applications: IApplication[]): IQuota2Type => {
  const admissionAreaCodesWithQuota2 = window.admissionAreaCodesWithQuota2 || [];
  const activeApplications = applications?.filter((a) => !a.response.cancelled && a.priority < (PRIORITY_MAX + 1)) || [];
  const found = activeApplications.filter((application) => admissionAreaCodesWithQuota2.includes(application.admissionAreaCode) && application.request.quota2Applicant);
  if (found.length === 0) {
    return {
      type: 'none',
    };
  }
  const nat = found.some((application) => application.admissionAreaCode.toString().charAt(2) === '1');
  return {
    type: nat ? 'nat' : 'health',
  };
};

export const getSourceForFrame = (
  serviceUrl: string,
  path: string,
  referrer: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback: (json: any) => void,
): void => {
  const getSource = async () => {
    const url = `${serviceUrl}?path=${path}&referrer=${referrer}`;
    const response = await fetch(url, {
      credentials: 'include',
    });

    const text = await response.text();
    callback(text);
  };

  getSource();
};

export const getQuota2Cookie = (): string | null => getCookie(cookies.quota2) as string;

export const setQuota2Cookie = (): void => {
  setCookie(cookies.quota2, true, null, 365, `;path=/;domain=${window.location.hostname}`);
};
